import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const UploadLoadingScreen = ({ progress }) => {
    const [statusIndex, setStatusIndex] = useState(0);
    const [factIndex, setFactIndex] = useState(0);

    const statuses = [
        "Scanning handwriting strokes...",
        "Analyzing baseline and slant...",
        "Measuring pen pressure...",
        "Decoding spatial patterns...",
        "Uploading to graphology engine..."
    ];

    const postUploadStatuses = [
        "Upload Complete. Hold tight!",
        "Preparing your Brain Balance...",
        "Identifying Core Strengths...",
        "Unlocking your Superpower...",
        "Generating Personality Profile..."
    ];

    const facts = [
        "Large handwriting often indicates an extroverted personality.",
        "Small, precise writing can suggest strong focus and attention to detail.",
        "The way you cross your 't's can reveal your ambition levels.",
        "Heavy pen pressure typically signals high energy or emotional intensity.",
        "A rightward slant often indicates you are open to new experiences.",
        "Not connecting letters (printing) suggests a creative and intuitive mind.",
        "Wide spacing between words usually indicates a need for personal space.",
        "Rounding your letters can show creativity and artistic ability.",
        "Pointed letters often suggest an aggressive or intelligent personality.",
        "A signature that is different from the text shows a public vs private persona.",
        "Writing that slants to the left can indicate introspection and reserve."
    ];

    // Cycle through main statuses (Slower: 8s)
    useEffect(() => {
        const interval = setInterval(() => {
            setStatusIndex((prev) => (prev + 1) % (progress >= 100 ? postUploadStatuses.length : statuses.length));
        }, 8000);
        return () => clearInterval(interval);
    }, [progress, statuses.length, postUploadStatuses.length]);

    // Cycle through facts (Slower: 10s)
    useEffect(() => {
        const interval = setInterval(() => {
            setFactIndex((prev) => (prev + 1) % facts.length);
        }, 10000);
        return () => clearInterval(interval);
    }, [facts.length]);

    const currentStatus = progress >= 100 ? postUploadStatuses[statusIndex % postUploadStatuses.length] : statuses[statusIndex % statuses.length];

    return (
        <div className="flex flex-col items-center justify-center w-full min-h-[400px] relative overflow-hidden bg-transparent">
            {/* Background Matrix/Grid Effect */}
            <div className="absolute inset-0 opacity-10 bg-[linear-gradient(rgba(59,130,246,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.1)_1px,transparent_1px)] bg-[size:20px_20px]" />

            {/* Central Data Transfer Visual */}
            <div className="relative w-full max-w-md flex flex-col items-center z-10">

                {/* Animated "Packet" Stream - Enhanced */}
                <div className="w-full h-3 bg-gray-900/80 rounded-full mb-10 relative overflow-hidden flex items-center border border-gray-700/50 shadow-[0_0_10px_rgba(59,130,246,0.1)]">
                    {/* Base Progress Bar */}
                    <motion.div
                        className="h-full bg-gradient-to-r from-blue-600 to-purple-600 absolute left-0 top-0"
                        style={{ width: `${progress}%` }}
                        layoutId="progressBar"
                    />

                    {/* Glitch/Scanline Effect overlay */}
                    <motion.div
                        className="absolute inset-0 bg-white/10"
                        animate={{ x: ["-100%", "100%"] }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                </div>

                {/* Lock Icon Animation - SVG Replacement */}
                <div className="mb-8 relative flex items-center justify-center">
                    {/* Outer Rotating Cyber Ring */}
                    <motion.div
                        className="absolute w-28 h-28 border-[3px] border-transparent border-t-blue-500/60 border-b-purple-500/60 rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    />
                    <motion.div
                        className="absolute w-24 h-24 border-[1px] border-dashed border-white/20 rounded-full"
                        animate={{ rotate: -360 }}
                        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    />

                    {/* Central Brain SVG */}
                    <motion.div
                        className="relative z-10 w-14 h-14 text-purple-400 drop-shadow-[0_0_18px_rgba(168,85,247,0.9)]"
                        animate={{
                            scale: [1, 1.08, 1],
                            opacity: [0.85, 1, 0.85]
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
                            <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
                            <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
                        </svg>
                    </motion.div>

                    {/* Particle effects behind lock */}
                    <motion.div
                        className="absolute w-20 h-20 bg-blue-500/20 rounded-full blur-xl"
                        animate={{ scale: [0.8, 1.2, 0.8] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    />
                </div>

                {/* Dynamic Status Text */}
                <div className="text-center h-16 flex flex-col items-center mb-8">
                    <AnimatePresence mode="wait">
                        <motion.h2
                            key={currentStatus}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                            className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-2 tracking-wide"
                        >
                            {currentStatus}
                        </motion.h2>
                    </AnimatePresence>

                    <div className="flex items-center justify-center space-x-2">
                        <span className="text-xs text-gray-400 font-mono tracking-widest uppercase">
                            {progress < 100 ? `Transfer Rate: ${Math.round(progress * 1.5)} MB/s` : "Building your custom profile..."}
                        </span>

                        {/* Blinking Cursor */}
                        <motion.span
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ duration: 0.8, repeat: Infinity }}
                            className="w-1.5 h-3 bg-blue-500 block"
                        />
                    </div>
                </div>

                {/* "Did You Know?" Fact Card */}
                <div className="w-full max-w-xs md:max-w-sm mt-4">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={factIndex}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.5 }}
                            className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4 text-center relative overflow-hidden"
                        >
                            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500" />
                            <p className="text-blue-300 text-xs font-bold uppercase tracking-wider mb-2">Did You Know?</p>
                            <p className="text-gray-200 text-sm leading-relaxed px-2">
                                {facts[factIndex]}
                            </p>
                        </motion.div>
                    </AnimatePresence>
                </div>

            </div>
        </div>
    );
};
